import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FilterIcon, ChevronDownIcon, Download } from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { getDefaultAcademicYearRange } from '../../../utils/dashboardDateRange';
import { createReportCsvBlob } from '../../../utils/reportCsvExport';
import { getLearningLevelTranslation } from '../../../services/learningLevelsTranslationsService';
import { useReportOrgScope } from './useReportOrgScope';
import ReportOrgFilterFields from './ReportOrgFilterFields';
import {
  REPORT_FILTER_PANEL,
  reportFilterToggleClass,
  REPORT_INPUT,
  REPORT_LABEL,
  REPORT_BTN_APPLY,
  REPORT_BTN_CLEAR,
  REPORT_BODY,
  REPORT_SCOPE_ALERT,
  REPORT_PERIOD_IN_TOOLBAR,
  REPORT_TOOLBAR,
  REPORT_BTN_CSV,
  REPORT_TABLE_SHELL,
  REPORT_THEAD_ROW,
  ReportTh,
  REPORT_TBODY_ROW,
  REPORT_EMPTY,
  REPORT_TABLE_VALIGN
} from './ReportPageUi';
import DailyExercisesReportCharts from './DailyExercisesReportCharts';
import { buildDailyExercisesChartCsvAppend } from './dailyExercisesReportChartsUtils';
import { useReportChartFetch, useReportChartDisplay } from './useReportChartFetch';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500, 1000];
const PAGE_SIZE_ALL = 10000;
const CSV_FETCH_PAGE_SIZE = 1000;
const CSV_EXPORT_TIMEOUT_MS = 180000;
/** GET /games — معامل limit (مثل صفحة إدارة الألعاب؛ تجنّب قيم كبيرة يرفضها الخادم) */
const GAMES_CATALOG_PAGE_LIMIT = 10;
const GAMES_CATALOG_MAX_PAGES = 100;

/**
 * يجلب كل الألعاب من `/games` مع التصفح عبر data.pagination (pages, total).
 */
async function fetchGamesCatalog() {
  const all = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages && page <= GAMES_CATALOG_MAX_PAGES) {
    const res = await api.get('/games', {
      params: {
        page,
        limit: GAMES_CATALOG_PAGE_LIMIT
      }
    });
    if (!res.data?.success) {
      const msg = res.data?.message || res.data?.error;
      throw new Error(typeof msg === 'string' ? msg : 'games catalog failed');
    }
    const data = res.data.data || {};
    const batch = Array.isArray(data.games) ? data.games : [];
    all.push(...batch);

    const pag = data.pagination || {};
    if (page === 1) {
      const pages = Number(pag.pages);
      const total = Number(pag.total);
      if (Number.isFinite(pages) && pages > 0) {
        totalPages = pages;
      } else if (Number.isFinite(total) && total > 0) {
        totalPages = Math.max(1, Math.ceil(total / GAMES_CATALOG_PAGE_LIMIT));
      } else {
        totalPages = 1;
      }
    }

    if (batch.length === 0) break;
    if (page >= totalPages) break;
    page += 1;
  }

  const seen = new Set();
  return all.filter((g) => {
    if (!g || g.id == null) return false;
    if (seen.has(g.id)) return false;
    seen.add(g.id);
    if (g.is_deleted === 1 || g.is_deleted === true) return false;
    return true;
  });
}

const SCORE_BAND_VALUES = ['all', 'low', 'medium', 'high'];

function buildGamesReportParams(page, pageSize, applied, orgFilters) {
  const params = {
    page,
    pageSize: String(pageSize)
  };
  if (applied.dateFrom) params.dateFrom = applied.dateFrom;
  if (applied.dateTo) params.dateTo = applied.dateTo;
  if (applied.gameId) params.gameId = applied.gameId;
  params.scoreBand = applied.scoreBand || 'all';
  if (applied.learningLevelId) params.learningLevelId = applied.learningLevelId;
  if (orgFilters.countryId) params.countryId = orgFilters.countryId;
  if (orgFilters.regionId) params.regionId = orgFilters.regionId;
  if (orgFilters.academyId) params.academyId = orgFilters.academyId;
  if (orgFilters.districtId) params.districtId = orgFilters.districtId;
  if (orgFilters.institutionId) params.institutionId = orgFilters.institutionId;
  if (orgFilters.departmentId) params.departmentId = orgFilters.departmentId;
  return params;
}

function csvEscapeCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

async function fetchAllGamesRows(applied, orgFilters) {
  const rows = [];
  let pageNum = 1;
  const maxSafetyPages = 500;

  while (pageNum <= maxSafetyPages) {
    const params = buildGamesReportParams(pageNum, CSV_FETCH_PAGE_SIZE, applied, orgFilters);
    const response = await api.get('/reports/games', {
      params,
      timeout: CSV_EXPORT_TIMEOUT_MS
    });
    if (!response.data?.success) {
      const msg = response.data?.message || response.data?.error;
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg || 'export failed'));
    }
    const data = response.data.data || {};
    const batch = data.items || [];
    rows.push(...batch);

    if (batch.length === 0) break;
    if (batch.length < CSV_FETCH_PAGE_SIZE) break;

    pageNum += 1;
  }
  return rows;
}

const ReportGames = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const org = useReportOrgScope(currentLanguage);
  const defaultRange = getDefaultAcademicYearRange();
  const initialDateFrom = searchParams.get('dateFrom') || defaultRange.dateFrom;
  const initialDateTo = searchParams.get('dateTo') || defaultRange.dateTo;

  const [draft, setDraft] = useState({
    gameId: '',
    learningLevelId: '',
    scoreBand: 'all',
    dateFrom: initialDateFrom,
    dateTo: initialDateTo
  });
  const [applied, setApplied] = useState({
    gameId: '',
    learningLevelId: '',
    scoreBand: 'all',
    dateFrom: initialDateFrom,
    dateTo: initialDateTo
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 0 });
  const [period, setPeriod] = useState(null);
  const [hasScope, setHasScope] = useState(true);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [learningLevels, setLearningLevels] = useState([]);
  const [games, setGames] = useState([]);

  const langKey = currentLanguage === 'ar' ? 'ar' : currentLanguage === 'fr' ? 'fr' : 'en';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/organization/learning-levels?page=1&limit=100');
        if (!cancelled && res.data?.success) {
          setLearningLevels(res.data.data?.learningLevels || []);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchGamesCatalog();
        if (!cancelled) setGames(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          const msg = e.response?.data?.message || e.response?.data?.error || e.message;
          toast.error(msg ? String(msg) : t('reports.gamesReport.gamesLoadError'), { position: 'top-center' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const urlDateFrom = searchParams.get('dateFrom');
  const urlDateTo = searchParams.get('dateTo');
  useEffect(() => {
    if (urlDateFrom && urlDateTo) {
      setDraft((prev) => ({ ...prev, dateFrom: urlDateFrom, dateTo: urlDateTo }));
      setApplied((prev) => ({ ...prev, dateFrom: urlDateFrom, dateTo: urlDateTo }));
      setPage(1);
    }
  }, [urlDateFrom, urlDateTo]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildGamesReportParams(page, pageSize, applied, org.appliedFilters);
      const response = await api.get('/reports/games', { params });
      if (response.data.success) {
        const data = response.data.data || {};
        setHasScope(data.hasScope !== false);
        setItems(data.items || []);
        const p = data.pagination || {};
        setPagination({
          page: Number(p.page) || page,
          pageSize: Number(p.pageSize) || pageSize,
          total: Number(p.total) || 0,
          totalPages: Number(p.totalPages) || 0
        });
        setPeriod(data.period || null);
      } else {
        setItems([]);
        setPagination({ page: 1, pageSize, total: 0, totalPages: 0 });
        setPeriod(null);
      }
    } catch (error) {
      const resData = error.response?.data;
      const serverMsg = resData?.message || resData?.error || resData?.errors;
      const detail = Array.isArray(serverMsg)
        ? serverMsg.map((e) => (typeof e === 'string' ? e : e?.message || JSON.stringify(e))).join(', ')
        : typeof serverMsg === 'object' && serverMsg !== null
          ? JSON.stringify(serverMsg)
          : serverMsg;
      if (detail) toast.error(String(detail), { duration: 5000, position: 'top-center' });
      else toast.error(t('reports.fetchError'), { duration: 3000, position: 'top-center' });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, applied, org.appliedFilters, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartFiltersKey = useMemo(
    () =>
      [
        applied.gameId,
        applied.dateFrom,
        applied.dateTo,
        applied.learningLevelId,
        applied.scoreBand,
        org.appliedFilters?.countryId,
        org.appliedFilters?.regionId,
        org.appliedFilters?.academyId,
        org.appliedFilters?.districtId,
        org.appliedFilters?.institutionId,
        org.appliedFilters?.departmentId
      ].join('|'),
    [applied, org.appliedFilters]
  );

  const { chartRows, chartLoading } = useReportChartFetch(
    chartFiltersKey,
    fetchAllGamesRows,
    applied,
    org.appliedFilters
  );
  const { rowsForCharts, chartsShowLoading, partial: chartsPartial } = useReportChartDisplay(
    chartRows,
    chartLoading,
    items,
    loading
  );

  const applyFiltersHandler = () => {
    org.applyOrgFilters();
    setApplied({ ...draft });
    setPage(1);
    org.setShowFilters(false);
  };

  const clearExtraFilters = () => {
    const r = getDefaultAcademicYearRange();
    setDraft((prev) => ({
      ...prev,
      gameId: '',
      learningLevelId: '',
      scoreBand: 'all',
      dateFrom: r.dateFrom,
      dateTo: r.dateTo
    }));
    setApplied((prev) => ({
      ...prev,
      gameId: '',
      learningLevelId: '',
      scoreBand: 'all',
      dateFrom: r.dateFrom,
      dateTo: r.dateTo
    }));
    setPage(1);
    org.setShowFilters(true);
  };

  const formatLevelName = useCallback(
    (name) => {
      if (!name || !String(name).trim()) return '—';
      const tr = getLearningLevelTranslation(String(name).trim());
      if (tr && tr[langKey]) return tr[langKey];
      return name;
    },
    [langKey]
  );

  const formatMs = (ms) => {
    if (ms == null || ms === '') return '—';
    const n = Number(ms);
    if (Number.isNaN(n)) return String(ms);
    const sec = Math.round(n / 1000);
    if (sec < 60) return `${sec} ${t('reports.seconds')}`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')} ${t('reports.minutes')}`;
  };

  const downloadCsv = async () => {
    if (exportingCsv) return;
    setExportingCsv(true);
    try {
      const allRows = await fetchAllGamesRows(applied, org.appliedFilters);
      if (allRows.length === 0) {
        toast.error(t('reports.users.csvNoRows'), { position: 'top-center' });
        return;
      }
      const headers = [
        t('reports.users.colFullName'),
        t('reports.users.colUsername'),
        t('reports.users.colEmail'),
        t('reports.users.colPhone'),
        t('reports.dailyExercisesApi.colLevel'),
        t('reports.dailyExercisesApi.colDepartment'),
        t('reports.dailyExercisesApi.colTotalSessions'),
        t('reports.dailyExercisesApi.colAvgSessionPct'),
        t('reports.dailyExercisesApi.colSuccessRatePct'),
        t('reports.dailyExercisesApi.colBestScore'),
        t('reports.dailyExercisesApi.colBestTime'),
        t('reports.dailyExercisesApi.colWorstScore'),
        t('reports.dailyExercisesApi.colWorstTime'),
        t('reports.dailyExercisesApi.colRankDept'),
        t('reports.dailyExercisesApi.colRankLevelInst')
      ];
      const lines = [headers.map(csvEscapeCell).join(',')];
      for (const row of allRows) {
        const line = [
          row.full_name || '',
          row.username || '',
          row.email || '',
          row.phone || '',
          (row.soroban_level_name
            ? formatLevelName(row.soroban_level_name)
            : (row.soroban_level_number != null ? String(row.soroban_level_number) : '')),
          row.department_name || '',
          row.total_sessions ?? '',
          row.avg_session_pct ?? '',
          row.success_rate_pct ?? '',
          row.best_session_score ?? '',
          formatMs(row.best_session_time_ms),
          row.worst_session_score ?? '',
          formatMs(row.worst_session_time_ms),
          row.rank_in_department ?? '',
          row.rank_in_level_institution ?? ''
        ].map(csvEscapeCell).join(',');
        lines.push(line);
      }
      const chartCsv = buildDailyExercisesChartCsvAppend(allRows, formatLevelName, t);
      const csvBody = `${lines.join('\r\n')}${chartCsv}`;
      const blob = createReportCsvBlob(csvBody);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `games-report-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('reports.users.csvSuccess', { count: allRows.length }), { position: 'top-center' });
    } catch (err) {
      const res = err.response?.data;
      const serverMsg = res?.message ?? res?.error ?? res?.errors;
      const detail = Array.isArray(serverMsg)
        ? serverMsg.map((e) => (typeof e === 'string' ? e : e?.message || JSON.stringify(e))).join(', ')
        : typeof serverMsg === 'object' && serverMsg !== null
          ? JSON.stringify(serverMsg)
          : serverMsg;
      const axiosMsg = err.code === 'ECONNABORTED' || err.message?.includes('timeout')
        ? t('reports.users.csvTimeout')
        : null;
      toast.error(axiosMsg || (detail ? String(detail) : err.message) || t('reports.users.csvError'), { duration: 6000, position: 'top-center' });
    } finally {
      setExportingCsv(false);
    }
  };

  const canExportCsv = !loading && !exportingCsv && (pagination.total > 0 || items.length > 0);

  const studentGamesSessionsTo = (row) => {
    const sid = row.student_id != null ? row.student_id : row.user_id;
    if (sid == null || sid === '') return null;
    const q = new URLSearchParams();
    if (applied.dateFrom) q.set('dateFrom', applied.dateFrom);
    if (applied.dateTo) q.set('dateTo', applied.dateTo);
    const qs = q.toString();
    return `/dashboard/reports/games/students/${encodeURIComponent(String(sid))}${qs ? `?${qs}` : ''}`;
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-sm shadow-slate-900/5 ring-1 ring-slate-900/[0.03]">
      <div className={REPORT_FILTER_PANEL}>
        <button
          type="button"
          onClick={() => org.setShowFilters(!org.showFilters)}
          className={reportFilterToggleClass(org.showFilters)}
        >
          <FilterIcon className="h-4 w-4 shrink-0 text-primary-600" />
          {t('common.filters')}
          <ChevronDownIcon className={`h-4 w-4 shrink-0 opacity-60 transition-transform ${org.showFilters ? 'rotate-180' : ''}`} />
        </button>
        {org.showFilters && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div>
              <label className={REPORT_LABEL}>{t('reports.gamesReport.game')}</label>
              <select
                value={draft.gameId}
                onChange={(e) => setDraft((prev) => ({ ...prev, gameId: e.target.value }))}
                className={REPORT_INPUT}
              >
                <option value="">{t('reports.gamesReport.allGames')}</option>
                {games.map((g) => (
                  <option key={g.id} value={String(g.id)}>{g.name || g.id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={REPORT_LABEL}>{t('reports.dailyExercisesApi.learningLevel')}</label>
              <select
                value={draft.learningLevelId}
                onChange={(e) => setDraft((prev) => ({ ...prev, learningLevelId: e.target.value }))}
                className={REPORT_INPUT}
              >
                <option value="">{t('reports.dailyExercisesApi.allLevels')}</option>
                {learningLevels.map((lvl) => (
                  <option key={lvl.id} value={String(lvl.id)}>{lvl.name || lvl.id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={REPORT_LABEL}>{t('reports.dailyExercisesApi.scoreBand')}</label>
              <select
                value={draft.scoreBand}
                onChange={(e) => setDraft((prev) => ({ ...prev, scoreBand: e.target.value }))}
                className={REPORT_INPUT}
              >
                {SCORE_BAND_VALUES.map((v) => (
                  <option key={v} value={v}>{t(`reports.dailyExercisesApi.scoreBand_${v}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={REPORT_LABEL}>{t('reports.dateFrom')}</label>
              <input
                type="date"
                value={draft.dateFrom}
                onChange={(e) => setDraft((prev) => ({ ...prev, dateFrom: e.target.value }))}
                className={REPORT_INPUT}
              />
            </div>
            <div>
              <label className={REPORT_LABEL}>{t('reports.dateTo')}</label>
              <input
                type="date"
                value={draft.dateTo}
                onChange={(e) => setDraft((prev) => ({ ...prev, dateTo: e.target.value }))}
                className={REPORT_INPUT}
              />
            </div>
            <ReportOrgFilterFields
              filters={org.filters}
              setFilters={org.setFilters}
              lockedOrgLevels={org.lockedOrgLevels}
              countries={org.countries}
              regions={org.regions}
              academies={org.academies}
              districts={org.districts}
              institutions={org.institutions}
              departments={org.departments}
              getCountryLabel={(c) => org.getCountryTranslation(c)}
            />
          </div>
        )}
        {org.showFilters && (
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={applyFiltersHandler}
              className={REPORT_BTN_APPLY}
            >
              <FilterIcon className="h-4 w-4" />
              {t('reports.applyFilters')}
            </button>
            <button
              type="button"
              onClick={clearExtraFilters}
              className={REPORT_BTN_CLEAR}
            >
              {t('common.clearFilters')}
            </button>
          </div>
        )}
      </div>

      <div className={REPORT_BODY}>
        {!hasScope && (
          <p className={`${REPORT_SCOPE_ALERT} mb-1`}>
            {t('reports.users.noScope')}
          </p>
        )}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : items.length === 0 ? (
          <p className={REPORT_EMPTY}>{t('reports.noData')}</p>
        ) : (
          <>
            <div className={REPORT_TOOLBAR}>
              <div className={REPORT_PERIOD_IN_TOOLBAR}>
                {period?.dateFrom && period?.dateTo && (
                  <>
                    <span className="font-semibold text-slate-600">{t('reports.dailyExercisesApi.period')}</span>
                    {' '}
                    <span className="tabular-nums text-slate-800">{period.dateFrom} → {period.dateTo}</span>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={downloadCsv}
                disabled={!canExportCsv}
                className={`${REPORT_BTN_CSV} shrink-0`}
              >
                <Download className="h-4 w-4" />
                {exportingCsv ? t('reports.users.csvExporting') : t('reports.users.downloadCsv')}
              </button>
            </div>
            <DailyExercisesReportCharts
              rows={rowsForCharts}
              loading={chartsShowLoading}
              partial={chartsPartial}
              formatLevelName={formatLevelName}
              t={t}
            />
            <div className={REPORT_TABLE_SHELL}>
              <div className="overflow-x-auto max-w-full">
                <table className={`min-w-[1100px] w-full text-sm ${REPORT_TABLE_VALIGN}`}>
                  <thead>
                    <tr className={REPORT_THEAD_ROW}>
                      <ReportTh>{t('reports.users.colFullName')}</ReportTh>
                      <ReportTh>{t('reports.users.colUsername')}</ReportTh>
                      <ReportTh>{t('reports.dailyExercisesApi.colLevel')}</ReportTh>
                      <ReportTh>{t('reports.dailyExercisesApi.colDepartment')}</ReportTh>
                      <ReportTh>{t('reports.dailyExercisesApi.colTotalSessions')}</ReportTh>
                      <ReportTh>{t('reports.dailyExercisesApi.colAvgSessionPct')}</ReportTh>
                      <ReportTh>{t('reports.dailyExercisesApi.colSuccessRatePct')}</ReportTh>
                      <ReportTh>{t('reports.dailyExercisesApi.colBestScore')}</ReportTh>
                      <ReportTh>{t('reports.dailyExercisesApi.colBestTime')}</ReportTh>
                      <ReportTh>{t('reports.dailyExercisesApi.colRankDept')}</ReportTh>
                      <ReportTh>{t('reports.dailyExercisesApi.colRankLevelInst')}</ReportTh>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {items.map((row) => (
                      <tr key={row.user_id} className={REPORT_TBODY_ROW}>
                        <td className="py-3 px-2 text-gray-900 font-medium">{row.full_name || '—'}</td>
                        <td className="py-3 px-2 text-gray-700">
                          {(() => {
                            const to = studentGamesSessionsTo(row);
                            const label = row.username || '—';
                            if (!to) return label;
                            return (
                              <Link to={to} className="text-primary-600 hover:text-primary-800 hover:underline font-medium">
                                {label}
                              </Link>
                            );
                          })()}
                        </td>
                        <td className="py-3 px-2 text-gray-700">
                          {row.soroban_level_name
                            ? formatLevelName(row.soroban_level_name)
                            : (row.soroban_level_number != null ? String(row.soroban_level_number) : '—')}
                        </td>
                        <td className="py-3 px-2 text-gray-700">{row.department_name || '—'}</td>
                        <td className="py-3 px-2 text-gray-700 tabular-nums">{row.total_sessions ?? '—'}</td>
                        <td className="py-3 px-2 text-gray-700 tabular-nums">{row.avg_session_pct ?? '—'}</td>
                        <td className="py-3 px-2 text-gray-700 tabular-nums">{row.success_rate_pct ?? '—'}</td>
                        <td className="py-3 px-2 text-gray-700 tabular-nums">{row.best_session_score ?? '—'}</td>
                        <td className="py-3 px-2 text-gray-700 tabular-nums">{formatMs(row.best_session_time_ms)}</td>
                        <td className="py-3 px-2 text-gray-700 tabular-nums">{row.rank_in_department ?? '—'}</td>
                        <td className="py-3 px-2 text-gray-700 tabular-nums">{row.rank_in_level_institution ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600">
              <p>
                {t('reports.users.pageInfo', {
                  page: pagination.page,
                  totalPages: pagination.totalPages || 1,
                  total: pagination.total
                })}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2">
                  <span>{t('reports.users.pageSize')}</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="rounded-md border border-gray-300 py-1.5 px-2 text-sm min-w-[10rem]"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                    <option value={PAGE_SIZE_ALL}>{t('reports.users.pageSizeOptionAll')}</option>
                  </select>
                </label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                  >
                    {t('reports.users.prev')}
                  </button>
                  <button
                    type="button"
                    disabled={page >= (pagination.totalPages || 1)}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                  >
                    {t('reports.users.next')}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportGames;
